import { useEffect, useRef, useState } from 'react';
import { IGameItem } from '@/utils/types/Items';
import { rarityColor } from '@/utils/rarity';
import { iconBackgroundStyle } from '@/utils/iconBackground';

interface Props {
    skins: IGameItem[];
    /** Tile (and +N chip) size in px. */
    tile?: number;
    /** Gap between tiles in px. */
    gap?: number;
    /** Corner radius in px. */
    radius?: number;
    /** Hide the row on mobile (jackpot panels do; coinflip rows show it). */
    mobileHidden?: boolean;
    /** Extra classes for the flex container (e.g. z-index/positioning). */
    className?: string;
}

/**
 * Renders skin tiles, fitting AS MANY as the container width allows — no fixed
 * cap. When they don't all fit, the last slot becomes a "+N" chip whose N is
 * computed from how many tiles actually fit (so it always reflects the exact
 * hidden count at the current screen/column width).
 */
const SkinsRow = ({
    skins,
    tile = 50,
    gap = 8,
    radius = 10,
    mobileHidden = true,
    className = '',
}: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const measure = () => setWidth(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const total = skins.length;
    let visible = total;
    let extra = 0;
    if (width > 0 && total > 0) {
        // How many tiles fit edge-to-edge: n*tile + (n-1)*gap <= width.
        const fitAll = Math.max(0, Math.floor((width + gap) / (tile + gap)));
        if (fitAll < total) {
            // Reserve one slot for the +N chip, then show the rest.
            visible = Math.max(0, fitAll - 1);
            extra = total - visible;
        }
    }

    return (
        <div
            ref={ref}
            className={`flex ${mobileHidden ? 'max-md:hidden' : ''} ${className}`}
            style={{ gap }}
        >
            {skins.slice(0, visible).map((skin, j) => {
                const rarity = rarityColor(skin.colorHex);
                return (
                    <div
                        key={`${skin.id}-${j}`}
                        data-tip="1"
                        data-name={skin.name}
                        data-price={skin.priceUsd}
                        data-rarity={rarity}
                        data-image={skin.icon}
                        data-icon-background={skin.iconBackground ?? ''}
                        style={
                            {
                                width: tile,
                                height: tile,
                                borderRadius: radius,
                                ['--rarity' as string]: rarity,
                                // Subtle fill in the skin's rarity colour.
                                background: `linear-gradient(160deg, color-mix(in srgb, ${rarity} 22%, transparent), color-mix(in srgb, ${rarity} 5%, transparent))`,
                                borderColor: `color-mix(in srgb, ${rarity} 30%, transparent)`,
                            } as React.CSSProperties
                        }
                        className="relative border grid place-items-center shrink-0 cursor-pointer overflow-hidden transition-all hover:-translate-y-0.5 hover:[border-color:var(--rarity)] hover:shadow-[inset_0_0_14px_-3px_var(--rarity),0_0_0_1.5px_var(--rarity)]"
                    >
                        {skin.icon && (
                            <img
                                src={skin.icon}
                                alt=""
                                className="w-[88%] h-[88%] object-contain"
                                style={iconBackgroundStyle(skin.iconBackground)}
                            />
                        )}
                    </div>
                );
            })}
            {extra > 0 && (
                <div
                    style={{ width: tile, height: tile, borderRadius: radius }}
                    className="bg-layer_2 border border-stroke grid place-items-center shrink-0"
                >
                    <span className="text-sm font-extrabold text-text_3">+{extra}</span>
                </div>
            )}
        </div>
    );
};

export default SkinsRow;
